import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import {
    BrowserRouter,
} from "react-router-dom";

import { AuthProvider } from "./common/hooks/useAuth";
import QVRouter from "./routes/QVRouter";
import { AppTheme } from './common/ui/customisation/AppTheme';
import { Storage } from './common/utils/Storage';
import UserDeletedHandler from './common/ui/qvshop/components/UserDeletedHandler';
import ShopConfig from './common/api/config/shopConfig';

import DateTimeFormatConfig from './common/utils/DateTimeFormatConfig';
import { ShopDatesInterval } from './common/utils/ShopDatesInterval';
import WorkingDays from './common/utils/WorkingDays';
import RecurrenceTypes from './common/ui/qvshop/components/recurringIntervalSelector/utils/RecurrenceTypes';
import RecurrenceDays from './common/ui/qvshop/components/recurringIntervalSelector/utils/RecurrenceDays';

import { getSettings, selectWorkingHours, selectSemesterSettings, selectOrganisationName, selectHasExtendedTimes, selectAllowMultiLocationCart, selectClosingDays, selectLimitRentalDays, selectBrand, selectWarehouseLocations } from "./common/store/settingsSlice";
import { getUserWarehouseLocations, selectUserHasLocations, selectUserLocations, selectUserLang, selectIsLoadingUserLocations } from './common/store/locationsSlice';
import { fetchAvailableLanguages, applyLanguage, persistDefaultLanguage, selectAvailableLanguages, selectAvailableLanguagesLoaded } from './common/store/languageSlice';
import { getCategories } from './common/store/categoriesSlice';
import { 
    selectRecurrenceType,
    selectStartDate, 
    selectEndDate,

    setStartDate,
    setEndDate,
    changeRecurrenceType,
    setIsCurrentSemesterInProgress, 
    setIsCurrentSemesterNoClassesInProgress, 
    setHasNoClasses, 
    setStartDateRecurring,
    setEndDateRecurring,
    setStartPickerEnabledIntervals,
    setEndPickerEnabledIntervals,
    setStartPickerEnabledHoursForDay,
    setEndPickerEnabledHoursForDay,

    setLocation,

} from './common/store/filtersSlice';
import { getReturnKey, prepareReturnKeyData, setCart } from './common/store/cartSlice';
import i18n from './common/lang/config';

export default function App() {
    dayjs.locale(i18n.language || 'de');

    const dispatch = useDispatch();
    const token = Storage.getAuthToken();
    const isIAMUser = Storage.isIAMUser();
    
    const organisationName = useSelector(selectOrganisationName);
    const brand = useSelector(selectBrand);
    const workingHours = useSelector(selectWorkingHours);
    const semesterSettings = useSelector(selectSemesterSettings);
    const hasExtendedTimes = useSelector(selectHasExtendedTimes);
    const limitRentalDays = useSelector(selectLimitRentalDays);

    const startDate = useSelector(selectStartDate);
    const endDate = useSelector(selectEndDate);

    const recurrenceType = useSelector(selectRecurrenceType);

    const userHasLocations = useSelector(selectUserHasLocations);
    const userLocations = useSelector(selectUserLocations);
    const userLang = useSelector(selectUserLang);
    const isLoadingUserLocations = useSelector(selectIsLoadingUserLocations);

    const availableLanguages = useSelector(selectAvailableLanguages);
    const availableLanguagesLoaded = useSelector(selectAvailableLanguagesLoaded);

    const allowMultiLocationCart = useSelector(selectAllowMultiLocationCart);
    const warehouseLocations = useSelector(selectWarehouseLocations);
    const closingDays = useSelector(selectClosingDays);
    const storageLocationId = Storage.getLocationId();
    
    useEffect(() => {
        document.title = organisationName + ' Verleihshop';
    }, [organisationName]);

    useEffect(() => {
        const faviconUrl = `${ShopConfig.config().BASE_URL}frontend_api/v2/?method=get_favicon&brand=${brand}&v=2`;
        const faviconLinks = document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']");

        faviconLinks.forEach((favicon) => favicon.remove());

        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        // The endpoint supplies the MIME type, including before settings finish loading.
        favicon.href = faviconUrl;
        document.head.appendChild(favicon);
    }, [brand]);

    useEffect(() => {
        dispatch(fetchAvailableLanguages());
    }, [dispatch]);

    useEffect(() => {
        // Only apply the user's server language if it is still offered by the server,
        // otherwise the fallback effect below picks an available language
        if (!availableLanguagesLoaded || !availableLanguages[userLang]) {
            return;
        }

        if (userLang !== Storage.getLanguage()) {
            dispatch(applyLanguage(userLang));
        }
    }, [dispatch, userLang, availableLanguages, availableLanguagesLoaded]);

    useEffect(() => {
        // Keep the active language within the languages offered by the server
        if (!availableLanguagesLoaded) {
            return;
        }

        const languageCodes = Object.keys(availableLanguages);
        const currentLanguage = i18n.language?.slice(0, 2);

        if (languageCodes.length > 0 && !languageCodes.includes(currentLanguage)) {
            dispatch(applyLanguage(languageCodes[0]));
        }
    }, [dispatch, availableLanguages, availableLanguagesLoaded]);

    // The user has no language on the server and never picked one: persist the defaulted language
    useEffect(() => {
        if (token && !isLoadingUserLocations && !userLang && !Storage.getLanguage()) {
            dispatch(persistDefaultLanguage(i18n.language.slice(0, 2)));
        }
    }, [dispatch, token, isLoadingUserLocations, userLang]);

    useEffect(() => {
        // Load settings on each app start
        dispatch(getSettings());
    }, [dispatch]);

    useEffect(() => {
        // if this is an IAM user, allow first for the iAM auth to complete, following that the warehouses are loaded after login
        // check LoginView.js
        if(token && !isIAMUser)
        {
            dispatch(getUserWarehouseLocations());
        }
    }, [dispatch, token, isIAMUser]);

    useEffect(() => {
        if(token)
        {
            const cartInStorage = Storage.getCart();
            dispatch(setCart(cartInStorage));
            if(hasExtendedTimes) {
                dispatch(prepareReturnKeyData());
            }
        }
    }, [dispatch, token, hasExtendedTimes]);

    // ANDY: If the user has only one location - set it as the selected location
    useEffect(() => {
        if(userHasLocations) {
            if(allowMultiLocationCart) {
                // Multi-location cart: keep location filter at "0" to fetch data across all locations
                Storage.setLocationId("0");
                dispatch(setLocation("0"));
            } else if(userLocations.length === 1){
                // Set the location id inside the local storage
                Storage.setLocationId(userLocations[0].id);
                dispatch(setLocation(userLocations[0].id));
            } else {
                const foundLocationFromStorageLocationId = userLocations.find(location => location.id === storageLocationId)

                // If we find this location from local stoarage insisde the userlocations - save it in state as the selected location
                if(foundLocationFromStorageLocationId?.id) {
                    dispatch(setLocation(foundLocationFromStorageLocationId.id));
                } else { // The user no longer has this location - change the value inside the local storage to default "0"
                    Storage.setLocationId("0");
                }
            }

            // After the location has been set load the categories and the return key
            dispatch(getCategories());
            dispatch(getReturnKey());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, userLocations, userHasLocations, allowMultiLocationCart]);

    // Guests (no token) never load user locations - auto-select if the tenant has only one location
    useEffect(() => {
        if (token || warehouseLocations.length !== 1) {
            return;
        }

        Storage.setLocationId(warehouseLocations[0].id);
        dispatch(setLocation(warehouseLocations[0].id));
    }, [dispatch, token, warehouseLocations]);

    useEffect(() => {
        if( workingHours && semesterSettings ) {
            const maxRentalDate = ShopDatesInterval.getMaxRentalDate(limitRentalDays, semesterSettings);

            // Set the NON recurring initial dates and times for date-time pickers if not set
            const calculatedDates = ShopDatesInterval.get(workingHours, semesterSettings, 0, 0, startDate, endDate, closingDays, maxRentalDate);
            dispatch(setStartDate(calculatedDates.startDate));
            dispatch(setEndDate(calculatedDates.endDate));

            // Set the initial dates for recurring
            let currentSemesterInProgress = WorkingDays.isTodayInSemesterOrInNoClassesInterval(semesterSettings);

            dispatch(setIsCurrentSemesterInProgress(currentSemesterInProgress.currentSemester));
            dispatch(setIsCurrentSemesterNoClassesInProgress(currentSemesterInProgress.currentNoClasses));
            dispatch(setHasNoClasses(currentSemesterInProgress.hasNoClasses));

            if (recurrenceType === null) {
                // Select current semester if we are inside a semester interval
                if(currentSemesterInProgress.currentSemester) {
                    dispatch(changeRecurrenceType(RecurrenceTypes.RECURRENCE_TYPE_CURRENT_SEMESTER));
                } else if(currentSemesterInProgress.currentNoClasses) { // Select current NO classes if we are inside a no classes interval
                    dispatch(changeRecurrenceType(RecurrenceTypes.RECURRENCE_TYPE_CURRENT_NO_CLASSES));
                } else { // If we are outside any of the intervals select the next interval
                    dispatch(changeRecurrenceType(RecurrenceTypes.RECURRENCE_TYPE_NEXT_SEMESTER));
                }
            }

            // Set the initial dates for recurring interval
            if(recurrenceType !== null) {
                const calculatedDates = ShopDatesInterval.get(workingHours, semesterSettings, 1/* recurring */, recurrenceType, undefined, undefined, closingDays, maxRentalDate);
                dispatch(setStartDateRecurring(calculatedDates.startDate));
                dispatch(setEndDateRecurring(calculatedDates.endDate));
            }

            const disabledDaysPickup = WorkingDays.generateEnabledIntervalsForNextYears(workingHours, semesterSettings, WorkingDays.getPickupDropOff(true), 1, closingDays);
            dispatch(setStartPickerEnabledIntervals(disabledDaysPickup));
            const disabledDaysDropoff = WorkingDays.generateEnabledIntervalsForNextYears(workingHours, semesterSettings, WorkingDays.getPickupDropOff(false), 1, closingDays);
            dispatch(setEndPickerEnabledIntervals(disabledDaysDropoff));

            const timesForPickupSelectedDate = WorkingDays.prepareTimeSliderDataForDay(workingHours, semesterSettings, DateTimeFormatConfig.getDateFromStandardPickerDateFormat(calculatedDates.startDate), RecurrenceDays.WORKING_HOURS_KEY_FOR_PICKUP);
            dispatch(setStartPickerEnabledHoursForDay(timesForPickupSelectedDate));
            const timesForDropoffSelectedDate = WorkingDays.prepareTimeSliderDataForDay(workingHours, semesterSettings, DateTimeFormatConfig.getDateFromStandardPickerDateFormat(calculatedDates.endDate), RecurrenceDays.WORKING_HOURS_KEY_FOR_DROPOFF);
            dispatch(setEndPickerEnabledHoursForDay(timesForDropoffSelectedDate));
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workingHours, semesterSettings, closingDays, limitRentalDays]);

    useEffect(() => {
        if( workingHours && semesterSettings ) {
            const maxRentalDate = ShopDatesInterval.getMaxRentalDate(limitRentalDays, semesterSettings);
            const endDateAsDate = DateTimeFormatConfig.getDateFromStandardPickerDateFormat(endDate);
            const calculatedDates = ShopDatesInterval.get(workingHours, semesterSettings, 0, 0, startDate, endDateAsDate.isValid() ? endDate : null, closingDays, maxRentalDate);

            // dispatch(setStartDate(calculatedDates.startDate));
            dispatch(setEndDate(calculatedDates.endDate));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workingHours, semesterSettings, startDate, closingDays, limitRentalDays]);

    return (
        <AppTheme brand={brand}>
            <BrowserRouter>
                <AuthProvider>
                    <UserDeletedHandler />
                    <QVRouter />
                </AuthProvider>
            </BrowserRouter>
        </AppTheme>
    );
}
